import React, { useCallback, useEffect, useState } from 'react';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { ConfirmModal } from '../../../../shared/ui/ConfirmModal';
import type { SmsTemplate } from '../../../../entities/sms-template';
import { pb } from '../../../../shared/api/pocketbase';
import { Button } from '../../../../shared/ui/Button';
import { SmsTemplateModal } from '../../../../features/admin-sms-template-form';

const SmsTemplatesSection: React.FC = () => {
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; template?: SmsTemplate } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SmsTemplate | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadTemplates = useCallback(() => {
    setLoading(true);
    pb.collection('sms_templates')
      .getFullList<SmsTemplate>({ sort: 'name', fields: 'id,name,text,created,updated' })
      .then(setTemplates)
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleDelete = useCallback((template: SmsTemplate) => {
    setDeletingId(template.id);
    pb.collection('sms_templates')
      .delete(template.id)
      .then(() => setTemplates(prev => prev.filter(t => t.id !== template.id)))
      .finally(() => setDeletingId(null));
  }, []);

  return (
    <div className="adm-section">
      <div className="adm-section-header adm-section-header-row">
        <div>
          <h2 className="adm-section-title">SMS-шаблоны</h2>
          <p className="adm-section-sub">{loading ? 'Загрузка...' : `${templates.length} шаблонов`}</p>
        </div>
        <Button variant="adm-add" type="button" onClick={() => setModal({ mode: 'add' })}>
          <IconPlus size={15} stroke={2.5} />Добавить
        </Button>
      </div>

      {loading ? (
        <div className="adm-loading">Загрузка...</div>
      ) : (
        <div className="adm-ut-wrap">
          <table className="adm-ut">
            <thead>
              <tr>
                <th>Название</th>
                <th>Текст</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {templates.map(template => (
                <tr key={template.id} className="adm-ut-row">
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span className="adm-ut-name">{template.name || '—'}</span>
                  </td>
                  <td>
                    <span className="adm-ut-email" style={{ display: 'block', maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {template.text || '—'}
                    </span>
                  </td>
                  <td className="adm-ut-actions-cell">
                    <div className="adm-ut-actions">
                      <Button
                        variant="adm-edit"
                        type="button"
                        title="Редактировать"
                        onClick={() => setModal({ mode: 'edit', template })}
                      >
                        <IconPencil size={13} stroke={2} />
                      </Button>
                      <Button
                        variant="adm-delete"
                        type="button"
                        title="Удалить"
                        disabled={deletingId === template.id}
                        onClick={() => setConfirmDelete(template)}
                      >
                        <IconTrash size={13} stroke={2} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {templates.length === 0 && (
                <tr><td colSpan={3} className="adm-empty">Шаблоны не найдены</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <SmsTemplateModal
          mode={modal.mode}
          initial={modal.template ?? null}
          onClose={() => setModal(null)}
          onSaved={updated => {
            if (updated) {
              setTemplates(prev =>
                modal.mode === 'edit'
                  ? prev.map(t => t.id === updated.id ? updated : t)
                  : [...prev, updated].sort((a, b) => a.name.localeCompare(b.name))
              );
            }
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Удалить шаблон?"
          content={<>Вы уверены, что хотите удалить <strong>{confirmDelete.name}</strong>? Это действие необратимо.</>}
          onConfirm={() => { handleDelete(confirmDelete); setConfirmDelete(null); }}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export { SmsTemplatesSection };
